import { useState, useEffect, useMemo } from 'react';
import { X, Lightbulb, Plus, ArrowRight } from 'lucide-react';
import { useDismissedTips } from '../../../hooks/useDismissedTips';

// Tip definitions with conditions
const CONTEXTUAL_TIPS = [
    {
        id: 'empty-canvas',
        condition: (nodes) => nodes.length === 0,
        title: 'Get Started',
        message: 'Start by adding a Campaign node. Campaigns are the foundation of your advertising funnel.',
        action: { label: 'Add Campaign', nodeType: 'campaign' },
        priority: 1
    },
    {
        id: 'campaign-no-adset',
        condition: (nodes, edges) => {
            const campaigns = nodes.filter(n => n.type === 'campaign');
            return campaigns.length > 0 && campaigns.some(c =>
                !edges.some(e => e.source === c.id && nodes.find(n => n.id === e.target)?.type === 'adset')
            );
        },
        title: 'Add Targeting',
        message: 'Your campaign needs an Ad Set. Ad Sets define who sees your ads through targeting settings.',
        action: { label: 'Add Ad Set', nodeType: 'adset' },
        priority: 2
    },
    {
        id: 'adset-no-ads',
        condition: (nodes, edges) => {
            const adsets = nodes.filter(n => n.type === 'adset');
            return adsets.length > 0 && adsets.some(a =>
                !edges.some(e => e.source === a.id && nodes.find(n => n.id === e.target)?.type === 'ad')
            );
        },
        title: 'Create Your Ads',
        message: 'Add ads to your Ad Set. Ads are the actual creative content your audience will see.',
        action: { label: 'Add Ad', nodeType: 'ad' },
        priority: 3
    },
    {
        id: 'missing-mofu',
        condition: (nodes) => {
            const stages = nodes.filter(n => n.type === 'campaign').map(n => n.data.funnelStage);
            return stages.includes('TOFU') && stages.includes('BOFU') && !stages.includes('MOFU');
        },
        title: 'Consider MOFU',
        message: 'You have awareness (TOFU) and conversion (BOFU) campaigns, but no consideration (MOFU) stage to nurture leads.',
        action: { label: 'Learn More', type: 'info' },
        priority: 4
    },
    {
        id: 'no-budget',
        condition: (nodes) => {
            const campaigns = nodes.filter(n => n.type === 'campaign');
            return campaigns.length > 0 && campaigns.some(c =>
                !c.data.budget?.amount || c.data.budget.amount <= 0
            );
        },
        title: 'Set Your Budget',
        message: 'One or more campaigns have no budget set. Set a daily or lifetime budget to control spending.',
        action: { label: 'Edit Campaign', type: 'edit' },
        priority: 2
    },
    {
        id: 'consider-abtest',
        condition: (nodes) => {
            const ads = nodes.filter(n => n.type === 'ad');
            const abtests = nodes.filter(n => n.type === 'abtest');
            return ads.length >= 2 && abtests.length === 0;
        },
        title: 'Try A/B Testing',
        message: 'You have multiple ads. Consider using an A/B Test node to automatically find your best performing creative.',
        action: { label: 'Add A/B Test', nodeType: 'abtest' },
        priority: 5
    },
    {
        id: 'multi-stage-no-pool',
        condition: (nodes) => {
            const stages = nodes.filter(n => n.type === 'campaign').map(n => n.data.funnelStage);
            const hasMultiStage = (stages.includes('TOFU') && stages.includes('MOFU')) ||
                                  (stages.includes('MOFU') && stages.includes('BOFU'));
            const hasPools = nodes.some(n => n.type === 'retargetingPool');
            return hasMultiStage && !hasPools;
        },
        title: 'Connect Your Stages',
        message: 'Add a Retargeting Pool to automatically move engaged users between funnel stages. This creates an automated audience flow.',
        action: { label: 'Add Retargeting Pool', nodeType: 'retargetingPool' },
        priority: 4
    },
    {
        id: 'bofu-no-segment',
        condition: (nodes) => {
            const hasBofu = nodes.some(n => n.type === 'campaign' && n.data.funnelStage === 'BOFU');
            const hasSegment = nodes.some(n => n.type === 'segment');
            return hasBofu && !hasSegment;
        },
        title: 'Target High-Intent Users',
        message: 'Create an Audience Segment to target users based on intent signals like engagement depth and website behavior.',
        action: { label: 'Add Segment', nodeType: 'segment' },
        priority: 5
    },
    {
        id: 'pool-no-connection',
        condition: (nodes, edges) => {
            const pools = nodes.filter(n => n.type === 'retargetingPool');
            return pools.length > 0 && pools.some(p =>
                !edges.some(e => e.source === p.id || e.target === p.id)
            );
        },
        title: 'Connect Your Pool',
        message: 'Your Retargeting Pool needs connections. Connect it from a source campaign and to a target Ad Set.',
        action: { label: 'Learn More', type: 'info' },
        priority: 3
    }
];

export default function ContextualTips({ nodes, edges, onAddNode, onEditNode }) {
    const { dismissedTips, dismissTip, isTipDismissed } = useDismissedTips();
    const [isMinimized, setIsMinimized] = useState(false);
    const [showDontShowAgain, setShowDontShowAgain] = useState(false);

    // Find the highest priority applicable tip
    const activeTip = useMemo(() => {
        const applicableTips = CONTEXTUAL_TIPS
            .filter(tip => !isTipDismissed(tip.id) && tip.condition(nodes, edges))
            .sort((a, b) => a.priority - b.priority);

        return applicableTips[0] || null;
    }, [nodes, edges, isTipDismissed]);

    // Don't render if no tip or minimized
    if (!activeTip) return null;

    const handleDismiss = (permanent = false) => {
        dismissTip(activeTip.id, permanent);
        setShowDontShowAgain(false);
    };

    const handleAction = () => {
        if (activeTip.action.nodeType && onAddNode) {
            onAddNode(activeTip.action.nodeType);
        } else if (activeTip.action.type === 'edit' && onEditNode) {
            // Find first campaign without budget
            const campaign = nodes.find(n =>
                n.type === 'campaign' && (!n.data.budget?.amount || n.data.budget.amount <= 0)
            );
            if (campaign) {
                onEditNode(campaign);
            }
        }
        handleDismiss();
    };

    if (isMinimized) {
        return (
            <button
                onClick={() => setIsMinimized(false)}
                style={{
                    position: 'fixed',
                    bottom: 'var(--space-4)',
                    right: 'var(--space-4)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    padding: 'var(--space-3)',
                    backgroundColor: 'var(--color-primary)',
                    color: 'white',
                    border: 'none',
                    borderRadius: 'var(--radius-full)',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    zIndex: 100
                }}
            >
                <Lightbulb size={20} />
            </button>
        );
    }

    return (
        <div style={{
            position: 'fixed',
            bottom: 'var(--space-4)',
            right: 'var(--space-4)',
            width: '320px',
            backgroundColor: 'var(--color-surface)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            border: '1px solid var(--color-border)',
            zIndex: 100,
            overflow: 'hidden'
        }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 'var(--space-3) var(--space-4)',
                backgroundColor: 'var(--primary-50)',
                borderBottom: '1px solid var(--color-border)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <Lightbulb size={18} style={{ color: 'var(--color-primary)' }} />
                    <span style={{ fontWeight: 600, color: 'var(--color-primary)' }}>
                        {activeTip.title}
                    </span>
                </div>
                <button
                    onClick={() => handleDismiss()}
                    style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '2px',
                        color: 'var(--color-text-tertiary)'
                    }}
                >
                    <X size={16} />
                </button>
            </div>

            {/* Content */}
            <div style={{ padding: 'var(--space-4)' }}>
                <p style={{
                    margin: '0 0 var(--space-3) 0',
                    fontSize: '0.875rem',
                    color: 'var(--color-text-secondary)',
                    lineHeight: 1.5
                }}>
                    {activeTip.message}
                </p>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                    {activeTip.action.nodeType && (
                        <button
                            onClick={handleAction}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-1)',
                                padding: 'var(--space-2) var(--space-3)',
                                backgroundColor: 'var(--color-primary)',
                                color: 'white',
                                border: 'none',
                                borderRadius: 'var(--radius-md)',
                                cursor: 'pointer',
                                fontSize: '0.8rem',
                                fontWeight: 500
                            }}
                        >
                            <Plus size={14} />
                            {activeTip.action.label}
                        </button>
                    )}

                    {activeTip.action.type === 'edit' && (
                        <button
                            onClick={handleAction}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-1)',
                                padding: 'var(--space-2) var(--space-3)',
                                backgroundColor: 'var(--color-primary)',
                                color: 'white',
                                border: 'none',
                                borderRadius: 'var(--radius-md)',
                                cursor: 'pointer',
                                fontSize: '0.8rem',
                                fontWeight: 500
                            }}
                        >
                            <ArrowRight size={14} />
                            {activeTip.action.label}
                        </button>
                    )}
                </div>
            </div>

            {/* Footer */}
            <div style={{
                padding: 'var(--space-2) var(--space-4)',
                borderTop: '1px solid var(--color-border)',
                backgroundColor: 'var(--color-bg-secondary)'
            }}>
                {showDontShowAgain ? (
                    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                        <button
                            onClick={() => handleDismiss(true)}
                            style={{
                                flex: 1,
                                padding: 'var(--space-1)',
                                border: 'none',
                                backgroundColor: 'transparent',
                                color: 'var(--color-error)',
                                cursor: 'pointer',
                                fontSize: '0.75rem'
                            }}
                        >
                            Don't show again
                        </button>
                        <button
                            onClick={() => setShowDontShowAgain(false)}
                            style={{
                                padding: 'var(--space-1) var(--space-2)',
                                border: 'none',
                                backgroundColor: 'transparent',
                                color: 'var(--color-text-tertiary)',
                                cursor: 'pointer',
                                fontSize: '0.75rem'
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                ) : (
                    <button
                        onClick={() => setShowDontShowAgain(true)}
                        style={{
                            width: '100%',
                            padding: 'var(--space-1)',
                            border: 'none',
                            backgroundColor: 'transparent',
                            color: 'var(--color-text-tertiary)',
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            textAlign: 'center'
                        }}
                    >
                        Dismiss this tip...
                    </button>
                )}
            </div>
        </div>
    );
}
